import { useEffect } from 'react';
import { useUserStore } from 'src/store/user-store';
import { useAvailableSites } from './api/use-available-sites';
import { UserRole } from 'src/types/enum/user-role';
import { SiteType } from 'src/types/enum/site-type';
import { useApplications } from 'src/features/applications/site/hooks/use-applications';

export const useSetInitialOrganizations = (isLoggedIn: boolean = true) => {
  const {
    directoryToken,
    directoryTokenLockspace,
    setDirectoryToken,
    setDirectoryTokenLockspace,
    setAdminToken,
    setRole,
    viewContext,
    setIsSSO,
    setMfaEnabled,
  } = useUserStore();

  const { data: availableSitesData, isLoading: isLoadingAvailableSites } =
    useAvailableSites(isLoggedIn);

  const hasAvailableSites =
    availableSitesData &&
    availableSitesData?.resources?.filter(
      site => site.type === SiteType.RESIDENTIAL,
    )?.length >= 1;

  const { data: apps, isLoading: isLoadingApps } = useApplications(
    isLoggedIn && !!directoryToken,
  );

  useEffect(() => {
    if (viewContext === 'site') {
      if (hasAvailableSites && !directoryToken) {
        const residentialSites = availableSitesData?.resources?.filter(
          site => site.type === SiteType.RESIDENTIAL,
        );
        setDirectoryToken(residentialSites[0]?.token || '');
        setIsSSO(false);
      }
    }
  }, [
    availableSitesData?.resources,
    directoryToken,
    hasAvailableSites,
    setDirectoryToken,
    setIsSSO,
    viewContext,
  ]);

  //check if the user has MFA enabled - if not - show activation dialog:
  useEffect(() => {
    if (availableSitesData?.resources) {
      const selectedSite = availableSitesData.resources.find(
        site => site.token === directoryToken,
      );

      if (selectedSite) {
        setMfaEnabled(selectedSite.mfaEnabled);
      }
    }
  }, [directoryToken, availableSitesData?.resources, setMfaEnabled]);

  useEffect(() => {
    if (viewContext === 'site' && !directoryTokenLockspace) {
      if (apps) {
        const directoryTokenLockspace =
          apps.find(app => app.name === 'Lockspace')?.resolvedParams?.[0]
            ?.metadata?.tenantToken || '';

        setDirectoryTokenLockspace(directoryTokenLockspace);
      }
    }
  }, [apps, setDirectoryTokenLockspace, viewContext, directoryTokenLockspace]);

  useEffect(() => {
    if (viewContext === 'site') {
      if (
        hasAvailableSites &&
        isLoggedIn &&
        !isLoadingAvailableSites &&
        !isLoadingApps
      ) {
        setRole(
          availableSitesData?.resources?.find(
            site => site.type === SiteType.RESIDENTIAL,
          )?.role || UserRole.VIEWER,
        );
        setAdminToken(
          availableSitesData?.resources?.find(
            site => site.type === SiteType.RESIDENTIAL,
          )?.siteAdminToken || '',
        );
        setIsSSO(false);
      }
    }
  }, [
    availableSitesData?.resources,
    hasAvailableSites,
    isLoadingAvailableSites,
    isLoadingApps,
    isLoggedIn,
    setAdminToken,
    setIsSSO,
    setRole,
    viewContext,
  ]);

  return {
    isLoadingAvailableSites,
  };
};
